import { DEGRADACAO_PARA_MODO_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { semBolhaOtimista } from '@modules/aawz-intelligence-v2/utils/bolha-otimista'
import {
  gravarRecusaDaSessao,
  lerRecusaDaSessao,
} from '@modules/aawz-intelligence-v2/utils/recusa-do-modo-agente-na-sessao'
import { STORE_PRINCIPAL as STORE } from '@modules/aawz-intelligence-v2/api/chat-event-handlers'

// A queda do Modo Agente para o Modo Chat quando o backend recusa o aceite
// por FUNCIONALIDADE não contratada.
//
// Mixin PRÓPRIO (e não mais um bloco em `view-chat-modo-agente.mixin.js`) pela
// mesma regra HARD que criou aquele arquivo: teto de 500 linhas. Ele fica
// aplicado lado a lado em `view-chat.vue` — os dois moram na MESMA instância,
// então `despacharMensagemModoAgente` chama `this.degradarParaModoChat` e o
// computed `modoAgenteLigado` lê `this.modoAgenteRecusadoPeloBackend` sem
// nenhuma ponte. O discriminante da recusa mora em
// `utils/recusa-de-funcionalidade.js`.
export const viewChatDegradacaoDeGateMixin = {
  // `data()` no MIXIN (e não no `data()` da view, onde moram
  // `retryIndisponivel`/`tentandoNovamente`): quem escreve neste campo é o
  // método abaixo, e uma propriedade não declarada no componente que usa o
  // mixin não seria REATIVA no Vue 2 — `modoAgenteLigado` nunca recomputaria
  // e a degradação não valeria para nada, em silêncio.
  data() {
    return {
      // O backend já recusou o Modo Agente por FUNCIONALIDADE nesta tela?
      // Ver `degradarParaModoChat` para o porquê de isto existir.
      modoAgenteRecusadoPeloBackend: false,
    }
  },
  // ★ [owner, 21/09/2026] A recusa lembrada pela SESSÃO da aba entra ANTES do
  // `created()` da view (hooks de mixin rodam primeiro): com ela, nem
  // `reidratarRunSeJaSelecionada` nem o primeiro envio pós-F5 voltam a
  // perguntar ao backend — sem `POST /runs` inútil, sem replay do run velho e
  // sem o aviso repetido a cada recarga.
  created() {
    if (lerRecusaDaSessao(this.idDoEscritorioDaSessao())) this.modoAgenteRecusadoPeloBackend = true
  },
  methods: {
    idDoEscritorioDaSessao() {
      const usuario = (typeof this.$currentUser === 'function' && this.$currentUser()) || {}
      const escritorio = usuario.escritorio || {}
      return escritorio.id_interno || null
    },
    /**
     * Prepara a queda para o Modo Chat: aprende a recusa, avisa (sem alarme) e
     * recolhe a bolha otimista que o aceite já gravou.
     *
     * ★ **`modoAgenteRecusadoPeloBackend` não é otimização — sem ele a queda
     * não funciona**, e isso foi MEDIDO na tela (17/09/2026): com o Modo
     * Agente ainda ligado, o `start` do turno de Modo Chat cria a conversa, o
     * `watch: selectedConversation` (`view-chat-modo-agente.mixin.js`) chama
     * `apiChat.reidratarRun`, e ela encontra um run `running` — o run do
     * PRÓPRIO turno de chat, que o backend grava com `tipo='chat'`. A
     * reidratação então abre o relay `GET /runs/{id}/eventos`, e
     * `_acompanharRun` chama `_abrirAbortController` — que ABORTA o stream do
     * `POST /chat` em curso. Resultado observado: a mensagem saía, a resposta
     * nunca chegava e `chatLoading` ficava `true` para sempre. Trocar um
     * snackbar que some por uma tela que pensa sem fim seria piorar.
     *
     * O escopo é a SESSÃO DA ABA (o `data()` mais o `sessionStorage` por
     * escritório, `utils/recusa-do-modo-agente-na-sessao.js`; nunca
     * `localStorage`): a autoridade sobre o gate continua sendo do backend —
     * uma aba nova volta a perguntar a ele, e a funcionalidade contratada no
     * meio do caminho volta sozinha ali. Até 21/09/2026 era só o `data()`, e
     * cada F5 repetia o `POST /runs`, o 403 e o aviso (pedido do owner).
     *
     * Efeito colateral bem-vindo: do segundo turno em diante — e depois do
     * F5 — não há mais `POST /runs` inútil nem aviso repetido: o consultor é
     * avisado UMA vez por aba.
     *
     * ★ A bolha existe porque `apiChatModoAgente.js::aceitarRun` faz
     * `addMessage({role:'user'})` ANTES do POST — é o que dá resposta
     * instantânea ao Enter. `apiChat.sendMessage` faz o MESMO ao redespachar,
     * e `ADD_MESSAGE` só deduplica por `id` (as duas nascem com `id: null`):
     * sem recolher a primeira, o consultor veria a própria frase duas vezes
     * seguidas — a degradação ficaria parecendo um envio duplicado.
     *
     * A remoção é deliberadamente ESTREITA: só a última mensagem, só se for do
     * usuário, só se ainda não tiver `id` do servidor e só se o conteúdo for o
     * texto que acabamos de tentar despachar. Qualquer coisa fora disso não é
     * a bolha deste turno e fica onde está — perder mensagem de conversa é
     * pior que mostrar uma repetida.
     *
     * @returns {boolean} sempre `true` — é o sinal de "redespache pelo Modo
     *   Chat" que `despacharMensagemModoAgente` devolve ao chamador.
     */
    degradarParaModoChat(text) {
      this.modoAgenteRecusadoPeloBackend = true
      gravarRecusaDaSessao(this.idDoEscritorioDaSessao())
      this.$showSnack({
        text: DEGRADACAO_PARA_MODO_CHAT.AVISO,
        color: 'warning',
        button: true,
      })
      this.recolherBolhaOtimista(text)
      return true
    },
    /**
     * Tira do thread a bolha que o aceite gravou quando o turno NÃO aconteceu.
     * A régua (e o porquê de ela ser estreita) mora em `utils/bolha-otimista`;
     * aqui fica só o despacho. O 409 "conversa ocupada"
     * (`view-chat-modo-agente.mixin.js`) chama o mesmo método.
     */
    recolherBolhaOtimista(text) {
      // ⚠️ [20/09/2026] `this.ns`, nunca o STORE fixo. Quem GRAVA a bolha é
      // `apiChatModoAgente::aceitarRun`, no `storeNamespace` do motor — que na
      // `/home-ia` é `iaConversa:<uuid>`. Lendo o namespace fixo (vazio ali),
      // `semBolhaOtimista` devolvia `null`, este método saía no `return` e a
      // bolha FICAVA: no 403 a pergunta aparecia duas vezes (a degradação
      // regrava a dela pelo Modo Chat) e no 409 ela ficava no thread como
      // enviada, com a resposta anterior logo abaixo. É a mesma armadilha dos
      // outros mixins da view — o fallback serve o host mínimo dos testes.
      const ns = this.ns || STORE
      const mensagens = this.$store.getters[`${ns}/messages`] || []
      const semABolha = semBolhaOtimista(mensagens, text)
      if (!semABolha) return
      this.$store.dispatch(`${ns}/setMessages`, semABolha)
    },
  },
}
